import * as React from 'react';

import classNames from 'classnames';

import 'css!./__styles__/Drawer';

//
// keep consistent with value in style
//
const DRAWER_ANIMATION_DURATION_MS = 225;

type Props = {
  open: boolean;
  width: number;
  anchor: 'left' | 'right';
  className: string;
  children: React.ReactChild;
  onRequestClose?: () => void;
};

type State = {
  closing: boolean;
  timer?: NodeJS.Timeout;
};

/**
 * A list of DOM elements that needs to be overflow-patched.
 * The values of this list are querySelectors used by document.querySelector.
 */
const PATCHED_DOM_ELEMENTS: string[] = ['html', 'body'];

export class Drawer extends React.Component<Props> {
  static defaultProps = {
    width: 240,
    anchor: 'left',
    className: '',
  };

  state: State = {
    timer: undefined,
    closing: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { open } = this.props;

    // After the drawer is opened, ensure focus is on the drawer.
    // So that the drawer can receive keydown events.
    if (open && !prevProps.open && this.drawerRef) {
      this.drawerRef.focus();
      this.patchDomOverflow();
    }

    if (!open && prevProps.open) {
      const timer = setTimeout(() => {
        this.setState({
          timer: undefined,
          closing: false,
        });
      }, DRAWER_ANIMATION_DURATION_MS);

      this.setState({ timer, closing: true }, () => {
        this.recoverDomOverflow();
      });
    }
  }

  componentWillUnmount() {
    if (this.state.timer) {
      clearTimeout(this.state.timer);
    }
  }

  handleRequestClose = () => {
    const { onRequestClose } = this.props;

    if (onRequestClose) {
      onRequestClose();
    }
  };

  handleBackdropAnimationEnd = () => {
    const { open } = this.props;

    if (!open) {
      this.setState({ closing: false });
    }
  };

  handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === this.backdropRef) {
      this.handleRequestClose();
    }
  };

  handleDrawerBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent drawer close when the drawer body is clicked
    // (cancel the close behavior triggered by the backdrop click)
    e.stopPropagation();
  };

  handleDrawerBodyKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.keyCode === 27 && e.target === this.drawerRef) {
      this.handleRequestClose();
    }
  };

  /**
   * Save previous overflow value of DOM elements such as html/body.
   */
  prevDomOverflows: { [key: string]: string } = {};

  backdropRef: HTMLDivElement | null = null;

  drawerRef: HTMLDivElement | null = null;

  /**
   * Disable scrolling
   */
  patchDomOverflow() {
    //
    // prepare for a possibility that we will be completely ejected before we
    // have a chance to recover. Would be nice to integrate with react-router
    // history; however InjectedRouter seems to be missing some of the newer
    // functionality that our existing react-router integration supports.
    //
    window.addEventListener('popstate', this.recoverDomOverflow);

    for (const selector of PATCHED_DOM_ELEMENTS) {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        this.prevDomOverflows[selector] = el.style.overflow;
        el.style.overflow = 'hidden';
      }
    }
  }

  /**
   * Recover scrolling
   */
  recoverDomOverflow = () => {
    //
    // cleanup state change event
    //
    window.removeEventListener('popstate', this.recoverDomOverflow);

    //
    // clear out dom overflow styles
    //
    for (const selector of Object.keys(this.prevDomOverflows)) {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        el.style.overflow = this.prevDomOverflows[selector];
        delete this.prevDomOverflows[selector];
      }
    }
  };

  render() {
    const { open, width, anchor, children, className } = this.props;
    const { closing } = this.state;

    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    return (
      <div className={`rc-Drawer ${className}`}>
        <div
          className={classNames({ backdrop: true, open, closing })}
          role="document"
          aria-hidden={true}
          ref={(node) => {
            this.backdropRef = node;
          }}
          onClick={this.handleBackdropClick}
          onAnimationEnd={this.handleBackdropAnimationEnd}
        >
          <div
            className={classNames('drawer', { open, right: anchor === 'right' })}
            tabIndex={-1}
            role="document"
            style={{ width, transform: `translateX(${anchor === 'right' ? width : -width}px)` }}
            onKeyDown={this.handleDrawerBodyKeyDown}
            ref={(node) => {
              this.drawerRef = node;
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
    /* eslint-enable */
  }
}
